import {useCallback} from 'react'
import {type Node, type Edge, useReactFlow} from 'reactflow'
import {getLayoutedElements as layoutUtilGetLayoutedElements} from '../FlowCanvas'
import {ulid} from 'ulid'
import {
  NodeType,
  EDGE_LABELS,
  META_DATA_DEFAULT,
  EDGE_TYPES,
  buildOptionsMetaData,
} from '../../../../../../Utils/Constant/FlowConstants'
import {flushSync} from 'react-dom'

interface UseFlowManipulationProps {
  nodes: Node[]
  edges: Edge[]
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>
  setEdges: (edges: Edge[]) => void
  pushToUndoStack?: (nodes: Node[], edges: Edge[]) => void
  isUndoRedoOperation?: React.MutableRefObject<boolean>
}

interface EdgeCreationParams {
  nodeType: string
  source: string
  target: string
  newNodeId: string
  newEndNodeId?: string
  originalData?: any
}

export const useFlowManipulation = ({
  nodes,
  edges,
  setNodes,
  setEdges,
  pushToUndoStack,
  isUndoRedoOperation,
}: UseFlowManipulationProps) => {
  const {setNodes: setNodesProgrammatic, setEdges: setEdgesProgrammatic} = useReactFlow()

  const addMetaDataToNode = useCallback((node: Node): Node => {
    const metaData = META_DATA_DEFAULT[node.type as keyof typeof META_DATA_DEFAULT]
    const options = buildOptionsMetaData(node.type ?? '') || null
    if (metaData !== undefined) {
      return {
        ...node,
        data: {
          ...node.data,
          metaData: {
            [String(node.type)]: metaData,
            ...options,
          },
        },
      }
    }
    return node
  }, [])

  const createNodeWithMetadata = useCallback(
    (nodeId: string, nodeType: string, position: any): Node => {
      const baseNode: Node = {
        id: nodeId,
        type: nodeType,
        position,
        data: {},
      }
      return addMetaDataToNode(baseNode)
    },
    [addMetaDataToNode]
  )

  const createEdgesForNode = useCallback(
    ({
      nodeType,
      source,
      target,
      newNodeId,
      newEndNodeId,
      originalData,
    }: EdgeCreationParams): Edge[] => {
      const baseEdgeProps = {type: EDGE_TYPES.SMOOTHSTEP}
      if (nodeType === NodeType.CONDITION && newEndNodeId) {
        return [
          {
            id: `${source}-${newNodeId}`,
            source,
            target: newNodeId,
            ...baseEdgeProps,
            data: originalData || {},
          },
          {
            id: `${newNodeId}-${EDGE_LABELS.NO}-${newEndNodeId}`,
            source: newNodeId,
            target: newEndNodeId,
            ...baseEdgeProps,
            data: {label: EDGE_LABELS.NO},
          },
          {
            id: `${newNodeId}-${EDGE_LABELS.YES}-${target}`,
            source: newNodeId,
            target: target,
            ...baseEdgeProps,
            data: {label: EDGE_LABELS.YES},
          },
        ]
      }
      return [
        {
          id: `${source}-${newNodeId}`,
          source,
          target: newNodeId,
          ...baseEdgeProps,
          data: originalData || {},
        },
        {
          id: `${newNodeId}-${target}`,
          source: newNodeId,
          target,
          ...baseEdgeProps,
        },
      ]
    },
    []
  )

  const createNodesForAddition = useCallback(
    (existingNodes: Node[], nodeType: string, position: any): Node[] => {
      const newNodeId = ulid()
      const newNode = createNodeWithMetadata(newNodeId, nodeType, position)
      let updatedNodes = [...existingNodes, newNode]
      if (nodeType === NodeType.CONDITION) {
        const endNodeId = `${ulid()}-${NodeType.END}`
        const endNode = createNodeWithMetadata(endNodeId, NodeType.END, position)
        updatedNodes = [...updatedNodes, endNode]
      }
      return updatedNodes
    },
    [createNodeWithMetadata]
  )

  const updateEdgesAfterNodeAddition = useCallback(
    (existingEdges: Edge[], source: string, target: string, newEdges: Edge[]): Edge[] => {
      const filteredEdges = existingEdges.filter(
        (edge) => !(edge.source === source && edge.target === target)
      )
      return [...filteredEdges, ...newEdges]
    },
    []
  )

  const addNodeBetweenEdges = useCallback(
    (nodeType: string, source: string, target: string, position: any) => {
      if (isUndoRedoOperation?.current) return
      if (pushToUndoStack) {
        pushToUndoStack(nodes, edges)
      }
      setNodes((prevNodes) => {
        const updatedNodes = createNodesForAddition(prevNodes, nodeType, position)

        const originalEdge = edges.find((edge) => edge.source === source && edge.target === target)
        const originalData = originalEdge?.data
        const newNodeId =
          updatedNodes[updatedNodes.length - (nodeType === NodeType.CONDITION ? 2 : 1)].id
        const newEndNodeId =
          nodeType === NodeType.CONDITION ? updatedNodes[updatedNodes.length - 1].id : undefined
        const newEdges = createEdgesForNode({
          nodeType,
          source,
          target,
          newNodeId,
          newEndNodeId,
          originalData,
        })
        const updatedEdges = updateEdgesAfterNodeAddition(edges, source, target, newEdges)
        const {nodes: layoutedNodes, edges: layoutedEdges} = layoutUtilGetLayoutedElements(
          updatedNodes,
          updatedEdges
        )

        setNodes(layoutedNodes)
        setEdges(layoutedEdges)

        setNodesProgrammatic(layoutedNodes)
        setEdgesProgrammatic(layoutedEdges)
        return layoutedNodes
      })
    },
    [
      nodes,
      edges,
      setNodes,
      setEdges,
      pushToUndoStack,
      isUndoRedoOperation,
      createNodesForAddition,
      createEdgesForNode,
      updateEdgesAfterNodeAddition,
      setNodesProgrammatic,
      setEdgesProgrammatic,
    ]
  )

  return {
    addNodeBetweenEdges,
    createNodeWithMetadata,
    createEdgesForNode,
  }
}
